import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import type {
  Child,
  CreateChildRequest,
  AssessmentQuestion,
  Assessment,
  StartAssessmentRequest,
  SaveResponseRequest,
  SubmitAssessmentResult,
  ChildAssessmentSummary,
} from '../types/assessment.types';

// Query key factories
export const childKeys = {
  all: ['children'] as const,
  list: () => [...childKeys.all, 'list'] as const,
  detail: (id: number) => [...childKeys.all, 'detail', id] as const,
  assessments: (childId: number) => [...childKeys.all, 'assessments', childId] as const,
};

export const assessmentKeys = {
  all: ['assessments'] as const,
  questions: (categoryKey: string, fromAge: number, toAge: number) =>
    [...assessmentKeys.all, 'questions', { categoryKey, fromAge, toAge }] as const,
  detail: (id: number) => [...assessmentKeys.all, 'detail', id] as const,
};

// --- Children hooks ---

export function useChildren() {
  return useQuery<Child[]>({
    queryKey: childKeys.list(),
    queryFn: () => api.getChildren(),
  });
}

export function useCreateChild() {
  const queryClient = useQueryClient();

  return useMutation<Child, Error, CreateChildRequest>({
    mutationFn: (data) => api.createChild(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: childKeys.all });
    },
  });
}

/**
 * Fetch a single child by ID.
 */
export function useChild(childId: number, enabled = true) {
  return useQuery<Child>({
    queryKey: childKeys.detail(childId),
    queryFn: async () => {
      const children = await api.getChildren();
      const child = children.find((c) => c.id === childId);
      if (!child) throw new Error('Child not found');
      return child;
    },
    enabled: enabled && childId > 0,
  });
}

/**
 * Fetch completed assessments for a child (for profile category cards).
 */
export function useChildAssessments(childId: number, enabled = true) {
  return useQuery<ChildAssessmentSummary[]>({
    queryKey: childKeys.assessments(childId),
    queryFn: () => api.getChildAssessments(childId),
    enabled: enabled && childId > 0,
  });
}

// --- Assessment hooks ---

export function useAssessmentQuestions(
  categoryKey: string,
  fromAge: number,
  toAge: number,
  enabled = true
) {
  return useQuery<AssessmentQuestion[]>({
    queryKey: assessmentKeys.questions(categoryKey, fromAge, toAge),
    queryFn: () => api.getAssessmentQuestions(categoryKey, fromAge, toAge),
    enabled,
  });
}

export function useStartAssessment() {
  return useMutation<Assessment, Error, StartAssessmentRequest>({
    mutationFn: (data) => api.startAssessment(data),
  });
}

export function useSaveResponse(assessmentId: number) {
  return useMutation<
    { id: number; questionId: number; response: string },
    Error,
    SaveResponseRequest
  >({
    mutationFn: (data) => api.saveAssessmentResponse(assessmentId, data),
  });
}

export function useSubmitAssessment() {
  const queryClient = useQueryClient();

  return useMutation<
    SubmitAssessmentResult,
    Error,
    {
      assessmentId: number;
      retake?: boolean;
      localHour?: number;
      ageRanges?: { fromAge: number; toAge: number }[];
    }
  >({
    mutationFn: ({ assessmentId, retake, localHour, ageRanges }) =>
      api.submitAssessment(assessmentId, { retake, localHour, ageRanges }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assessmentKeys.all });
      queryClient.invalidateQueries({ queryKey: childKeys.all });
    },
  });
}
